import {useMemo} from "react";
import {formatDateTime, parseFormattedDate} from "../../../common/DateUtil";
import {Box, Button, Card, CardActions, CardContent, Grid, MenuItem, Select, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {RowActions} from "./RowActionsComponent";
import type {Reservation} from "../NewReservationsOverview";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

function getComparableValue(row: Reservation, field: string): number {
    switch (field) {
        case "id": {
            return row.id ?? 0;
        }
        case "checkIn": {
            const checkIn = formatDateTime(row.fromDate, row.checkIn);
            return parseFormattedDate(checkIn)?.getTime?.() ?? 0;
        }
        case "checkOut": {
            const checkOut = formatDateTime(row.toDate, row.checkOut);
            return parseFormattedDate(checkOut)?.getTime?.() ?? 0;
        }
        case "price":
            return Number(row.price) || 0;
        case "advance":
            return Number(row.advance) || 0;
        default:
            return 0;
    }
}

export function MobileCardsView({
                             reservations,
                             t,
                             isHotelFlow,
                             navigateWithUuidTo,
                             handleOpenEditModal,
                             downloadContracts,
                             requestValidate,
                             requestCancel,
                             markAdvanceReceived,
                             sortField,
                             setSortField,
                             sortDir,
                             setSortDir,
                         }: {
    reservations: Reservation[];
    t: (k: string) => string;
    isHotelFlow?: boolean;
    navigateWithUuidTo: (entity: any, path: string) => void;
    handleOpenEditModal: (uuid: string) => void;
    downloadContracts: (uuid: string) => Promise<any>;
    requestValidate: (uuid: string) => void;
    requestCancel: (uuid: string) => void;
    markAdvanceReceived: (uuid: string) => Promise<any> | void;
    sortField: "id" | "checkIn" | "checkOut" | "price" | "advance";
    setSortField: (v: any) => void;
    sortDir: "asc" | "desc";
    setSortDir: (v: any) => void;
}) {
    const isAwaitingAdvance = (row) => (row?.status === "BOOKED_AWAIT_ADVANCE");
    const sorted = useMemo(() => {
        const copy = [...reservations];
        copy.sort((a, b) => {
            const reservationOne = getComparableValue(a, sortField);
            const reservationTwo = getComparableValue(b, sortField);
            if (reservationOne < reservationTwo) return sortDir === "asc" ? -1 : 1;
            if (reservationOne > reservationTwo) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return copy;
    }, [reservations, sortField, sortDir]);

    return (
        <Box>
            {/* Sort bar */}
            <Box sx={{display: "flex", gap: 1, p: 1}}>
                <Select
                    size="small"
                    value={sortField}
                    variant={"outlined"}
                    onChange={(e) => setSortField(e.target.value)}
                >
                    <MenuItem value="id">{t("newest")}</MenuItem>
                    <MenuItem value="checkIn">{t("check_in")}</MenuItem>
                    <MenuItem value="checkOut">{t("checkOut")}</MenuItem>
                    <MenuItem value="price">{t("price")}</MenuItem>
                    <MenuItem value="advance">{t("advance")}</MenuItem>
                </Select>
                <ToggleButtonGroup
                    size="small"
                    value={sortDir}
                    exclusive
                    onChange={(_, v) => v && setSortDir(v)}
                >
                    <ToggleButton value="asc">
                        <ArrowUpwardIcon fontSize="small"/>
                    </ToggleButton>
                    <ToggleButton value="desc">
                        <ArrowDownwardIcon fontSize="small"/>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Cards */}
            <Grid container spacing={1} sx={{p: 1}}>
                {sorted.map((reservation) => (
                    <Grid item xs={12} key={reservation.bookingNumber}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography variant="subtitle1">
                                        {formatDateTime(reservation.fromDate, reservation.checkIn)} → {formatDateTime(reservation.toDate, reservation.checkOut)}
                                    </Typography>
                                    {isAwaitingAdvance(reservation) && (
                                        <Tooltip
                                            placement="left"
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: "#fff",
                                                        color: "text.primary",
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        boxShadow: 1,
                                                    },
                                                },
                                            }}
                                            title={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <span>{t("awaiting_advance") || "Awaiting advance"}</span>
                                                    <Button
                                                        size="small"
                                                        data-testid="mark-advance-receveived-button"
                                                        variant="contained"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            markAdvanceReceived?.(reservation.uuid);
                                                        }}
                                                    >
                                                        {t("mark_advance_received") || "Mark received"}
                                                    </Button>
                                                </Box>
                                            }
                                        >
                                            <WarningAmberIcon color="warning" fontSize="small" />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body2">
                                    {t("owner")}: <Button variant="text" size="small"
                                                          onClick={() => navigateWithUuidTo(reservation?.personInformation, "klanten")}>{reservation?.personInformation?.name}</Button>
                                </Typography>
                                <Typography variant="body2">
                                        {t("cats")}: {reservation.animals?.map((animal: any) => (
                                    <Button key={animal.id} variant="text" size="small" onClick={() => navigateWithUuidTo(animal, "dieren")} sx={{mr: 0.5}}>
                                        {animal.name}
                                    </Button>
                                ))}

                                </Typography>
                                <Typography variant="body2">
                                    € {reservation.price} • {t("advance")}: € {reservation.advance}
                                </Typography>
                                {isHotelFlow && reservation.stayTypes?.length ? (
                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                        {t("stay_types")}: {reservation.stayTypes.join(", ")}
                                    </Typography>
                                ) :  <Typography variant="body2" sx={{mt: 0.5}}>
                                    {t("service_name")}: {reservation.serviceName}
                                </Typography>}
                            </CardContent>
                            <CardActions>
                                <RowActions
                                    row={reservation}
                                    t={t}
                                    handleOpenEditModal={handleOpenEditModal}
                                    downloadContracts={downloadContracts}
                                    requestValidate={requestValidate}
                                    requestCancel={requestCancel}
                                />
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
